import { importPublishedRulebook, NotificationType, type RulebookProfile } from "@next-platform/client-common-core";
import { findRulebookDraftIdByServerId } from "@next-platform/client-common-core/rulebook/editor/rulebookDraftStore";
import { RulebookStatus, RulebookVisibility } from "@next-platform/packages-ts-types";

import { ROUTES } from "@/constants";

export type EditTarget = {
  profile: RulebookProfile;
  navigate: (path: string) => void;
  displayToast: (toast: { type?: NotificationType; message: string }) => void;
  t: (key: string) => string;
  setStateIsOpening: (opening: boolean) => void;
};

/**
 * The same flow the My Rulebooks card uses: reuse the linked local draft when one exists,
 * otherwise pull the published rulebook into a fresh local draft, then open the editor on it.
 */
export function openDraftOrImport({ profile, navigate, displayToast, t, setStateIsOpening }: EditTarget): void {
  const linkedDraftId = findRulebookDraftIdByServerId({ rulebookId: profile.id });
  if (linkedDraftId !== null) {
    navigate(`${ROUTES.rulebookEditor}/${linkedDraftId}`);
    setStateIsOpening(false);
    return;
  }
  importPublishedRulebook({ rulebookId: profile.id })
    .then((result) => {
      if (result.status !== "imported") {
        displayToast({ type: NotificationType.error, message: t("rulebookEditor:savedCard.openFailed") });
        return;
      }
      navigate(`${ROUTES.rulebookEditor}/${result.draftId}`);
    })
    .catch(() => displayToast({ type: NotificationType.error, message: t("rulebookEditor:savedCard.openFailed") }))
    .finally(() => setStateIsOpening(false));
}

export const isRulebookShareable = ({ profile, isPublicSharingAllowed }: { profile: RulebookProfile; isPublicSharingAllowed: boolean }): boolean =>
  isPublicSharingAllowed && (!profile.isOwner || (profile.visibility === RulebookVisibility.PUBLIC && profile.status === RulebookStatus.ACTIVE));
